import {exists} from '@aboutyou/utils';
import {isClientTimeoutExceededError} from './grpc/code';

export const throwValidatedDecorator = <T extends object>(
  fn: () => Promise<T> | T,
  options?: {
    validateData?: (data: T) => void;
    validateError?: (error: unknown) => void;
    nonEssential?: boolean;
  },
  nonCriticalErrorMessagePatterns?: RegExp[],
): (() => Promise<T>) => {
  return () => {
    const result = fn();
    const promiseFn =
      result instanceof Promise ? result : Promise.resolve(result);

    return promiseFn.then(
      data => {
        try {
          options?.validateData?.(data);
        } catch (err) {
          throwValidationError(err);
        }

        return data;
      },
      error => {
        try {
          options?.validateError?.(error);
        } catch (error) {
          throwValidationError(error);
        }

        if (options?.nonEssential && isClientTimeoutExceededError(error)) {
          error.skipServerErrorLogging = true;
        }

        if (nonCriticalErrorMessagePatterns?.length) {
          const message = error?.message;
          const stack = error?.stack;
          if (
            (message || stack) &&
            nonCriticalErrorMessagePatterns.some(
              pattern => pattern.test(message) || pattern.test(stack),
            )
          ) {
            error.skipServerErrorLogging = true;
            error.nonCriticalErrorMessage = true;
          }
        }

        throw error;
      },
    );
  };
};

const VALIDATION_ERROR_ID = Symbol('VALIDATION_ERROR_ID');
type ValidationThrowableType = {
  __VALIDATION_ERROR_ID__: typeof VALIDATION_ERROR_ID;
  data: unknown;
};

export const isValidationError = (
  error: unknown | ValidationThrowableType,
): error is ValidationThrowableType => {
  return (
    exists(error) &&
    Object.prototype.hasOwnProperty.call(error, '__VALIDATION_ERROR_ID__') &&
    (error as ValidationThrowableType).__VALIDATION_ERROR_ID__ ===
      VALIDATION_ERROR_ID
  );
};

const throwValidationError = <T extends object>(data: T): never => {
  // eslint-disable-next-line no-throw-literal
  throw {
    __VALIDATION_ERROR_ID__: VALIDATION_ERROR_ID,
    data,
  };
};
